import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ComponentRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
    LoadingSkeletonComponent,
    LoadMoreButtonComponent,
    ProgressBarIndicatorComponent,
    SubpageHeaderComponent,
    TrackByItem,
} from '@components';
import { CaseStatus, HomeSectionLabel } from '@constants';
import { environment } from '@environments/environment';
import { UserClaims } from '@okta/okta-auth-js';
import { ContentService, DoceboEnrollment, KeplerDoceboService, KeplerSfdcService, UserService } from '@services';
import { SupportCase } from '@types';
import { calculateProgress, formatKitType, getProgressLabel } from '@utils';
import { catchError, combineLatest, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { KeplerDBStarterKitResponse } from 'src/app/@shared/services/kepler-db/kepler-db.interface';
import { KeplerDbService } from 'src/app/@shared/services/kepler-db/kepler-db.service';
import { EducationCoursesCallToAction } from 'src/app/education/education-courses/enums';
import { StarterKitTab } from 'src/app/resources/onboarding/starter-kit/starter-kit.interface';
import { EducationCoursesCardAdapter } from '../../education/education-courses/education-courses-card.adapter';
import { JumpBackInItem } from './home-jump-back-in.interface';

@Component({
    selector: 'app-home-jump-back-in',
    imports: [
        CommonModule,
        LoadMoreButtonComponent,
        LoadingSkeletonComponent,
        MatCardModule,
        MatIconModule,
        MatTooltipModule,
        ProgressBarIndicatorComponent,
        SubpageHeaderComponent,
    ],
    templateUrl: './home-jump-back-in.component.html',
    styleUrls: ['./home-jump-back-in.component.scss'],
    providers: [EducationCoursesCardAdapter],
})
export class HomeJumpBackInComponent implements OnInit, OnDestroy, TrackByItem<JumpBackInItem> {
    private cdr = inject(ChangeDetectorRef);
    private router = inject(Router);
    private userService = inject(UserService);
    private dbService = inject(KeplerDbService);
    private contentService = inject(ContentService);
    private doceboService = inject(KeplerDoceboService);
    private keplerSfdcService = inject(KeplerSfdcService);
    private cardAdapter = inject(EducationCoursesCardAdapter);

    /** h4 text for this section */
    title = HomeSectionLabel.JUMP_BACK_IN;
    /** ref to the subsection header */
    @ViewChild('jumpBackInHeaderRef') headerRef: ComponentRef<SubpageHeaderComponent>;
    /** starter kits with progress data */
    starterKits$: Observable<JumpBackInItem[]>;
    /** education courses data */
    courses$: Observable<JumpBackInItem[]>;
    /** cases data */
    cases$: Observable<JumpBackInItem[]>;
    /** combined starter kits and courses */
    jumpBackInItems$: Observable<JumpBackInItem[]>;
    /** number of items to show */
    length = 3;
    /** max number of items to show  */
    max = 9;
    /** modifier to apply to {@link length} when load more button is clicked */
    modifier = 3;
    /** whether or not the load more button is displayed */
    isLoadMoreDisplay = false;

    /** rxjs subscriptions that should get cleaned up when the component is destroyed */
    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.starterKits$ = this.getStarterKits();
        this.courses$ = this.getCourses();
        this.cases$ = this.getCases();
        // NOTE: Future items can go here but they need to be added to the code below and adapted
        this.jumpBackInItems$ = combineLatest([this.starterKits$, this.courses$, this.cases$]).pipe(
            map(([starterKits, courses, cases]) => {
                const alternatedItems: JumpBackInItem[] = [];
                const maxLength = Math.max(starterKits.length, courses.length, cases.length);

                for (let i = 0; i < maxLength; i++) {
                    if (i < starterKits.length) {
                        alternatedItems.push(starterKits[i]);
                    }
                    if (i < courses.length) {
                        alternatedItems.push(courses[i]);
                    }
                    if (i < cases.length) {
                        alternatedItems.push(cases[i]);
                    }
                }

                return alternatedItems;
            }),
            tap((combinedItems: JumpBackInItem[]) => {
                this.isLoadMoreDisplay = combinedItems.length > this.modifier;
                this.max = combinedItems.length;
                // console.debug('jump back in combined items', combinedItems);
            }),
            takeUntil(this.destroy$),
        );
    }

    /**
     * Refresh loading length
     * @param val - number to load
     */
    refreshLength(val: number): void {
        this.length = val;
        this.cdr.detectChanges();
    }

    /**
     * Track by Item
     * @param _index - item index
     * @param item - Jump Back In item
     * @returns number or string unique identifier
     */
    trackByItem(_index: number, item: JumpBackInItem): NonNullable<number | string> {
        return item.id;
    }

    /**
     * Get starter kits (can be Capability Accelerators) with progress data and convert them to Jump Back In Items
     * @returns Observable of starter kit items as Jump Back In items
     */
    private getStarterKits(): Observable<JumpBackInItem[]> {
        return combineLatest([this.contentService.capabilityAccelerators$, this.userService.isAuthenticated$]).pipe(
            switchMap(([starterKits, isAuthenticated]) => {
                const filteredKits = starterKits.filter(
                    (kit: StarterKitTab) => kit.type === 'starter_kit' || kit.type === 'capability_accelerator',
                );

                if (isAuthenticated) {
                    return this.userService.claims$.pipe(
                        switchMap((claims: UserClaims) =>
                            this.dbService.getStarterKitProgress(claims.email).pipe(
                                map((response: KeplerDBStarterKitResponse) => {
                                    return filteredKits
                                        .map((kit: StarterKitTab) => {
                                            const tabProgress = response?.progress?.find(
                                                (progress) => kit.id === progress.productId,
                                            );

                                            const percentComplete = tabProgress
                                                ? calculateProgress(kit, tabProgress)
                                                : 0;

                                            const label = getProgressLabel(kit.label, percentComplete);

                                            return {
                                                id: kit.id,
                                                title: formatKitType(kit.type), // Set type for the card tag or card title
                                                name: kit.name,
                                                url: kit.url,
                                                label,
                                                points: kit.points || 100,
                                                showProgressIndicator: true,
                                                type: kit.type,
                                                percentComplete,
                                            } as JumpBackInItem;
                                        })
                                        .filter((kit) => kit.percentComplete > 0 && kit.percentComplete < 100);
                                }),
                                catchError(() => of([])),
                            ),
                        ),
                    );
                }

                return of([]);
            }),
        );
    }

    /**
     * Get education courses from Docebo enrollments and convert them to Jump Back In Items
     * @returns Observable of course items as Jump back items
     */
    private getCourses(): Observable<JumpBackInItem[]> {
        return this.doceboService.userDetail$().pipe(
            map((userDetail) => userDetail?.data?.user_data?.user_id),
            switchMap((doceboUserId) => {
                if (!doceboUserId) {
                    return of([null, null]);
                }
                const enrollments$ = this.doceboService.enrollments$(doceboUserId);
                const isEmployee$ = this.userService.isEmployee$;
                return combineLatest([enrollments$, isEmployee$]);
            }),
            map(([doceboEnrollmentsByCode, isEmployee]) => {
                const doceboEnrollments =
                    doceboEnrollmentsByCode && 'beyond' in doceboEnrollmentsByCode
                        ? doceboEnrollmentsByCode.beyond
                        : null;
                return (
                    doceboEnrollments
                        ?.filter((enrollment: DoceboEnrollment) => {
                            return !(enrollment.type === 'learning_plan' && !enrollment.status);
                        })
                        ?.map((enrollment: DoceboEnrollment) => {
                            const educationCard = this.cardAdapter.fromEnrollment(enrollment, isEmployee);
                            return {
                                id: educationCard?.id?.toString(),
                                name: educationCard?.title,
                                url: educationCard?.url,
                                label: educationCard.cta,
                                points: 0, // NOTE: There are no points enabled at this time for MVP
                                title: educationCard?.subtitle[0],
                                showProgressIndicator: false,
                                education: {
                                    isEducationCourse: true,
                                    showPoints: false,
                                },
                                percentComplete: 100,
                            } as JumpBackInItem;
                        })
                        ?.filter(
                            (course: JumpBackInItem) =>
                                course.label === EducationCoursesCallToAction.START ||
                                course.label === EducationCoursesCallToAction.CONTINUE,
                        ) || []
                );
            }),
            catchError(() => of([])),
        );
    }

    /**
     * Get cases from user service and convert them to Jump Back In Items
     * @returns Observable of case items as Jump Back In items
     */
    private getCases(): Observable<JumpBackInItem[]> {
        return this.keplerSfdcService.supportCases$.pipe(
            map((cases: SupportCase[]) => {
                return (
                    cases
                        ?.filter(
                            (caseItem: SupportCase) =>
                                caseItem.status === CaseStatus.AWAITING_INFO ||
                                caseItem.status === CaseStatus.SOLUTION_PROPOSED,
                        )
                        ?.map((caseItem: SupportCase): JumpBackInItem => ({
                            id: caseItem.id,
                            title: 'Product Support',
                            name: `Case : ${caseItem.case}`,
                            url: `${environment.mySupport.url}?case=${caseItem.case}`,
                            label: 'View case',
                            points: 0,
                            showProgressIndicator: false,
                            supportCase: {
                                subject: caseItem.subject,
                                status: caseItem.status,
                            },
                            type: 'case',
                            percentComplete: 50, // Default progress for cases
                        })) || []
                );
            }),
            catchError(() => of([])),
        );
    }

    /**
     * Handle on Card click for Jump back in
     * Handle internal links as routes and externa
     * @param card - Recommendation card
     */
    onCardClick(card: JumpBackInItem): void {
        if (!card?.url) {
            return;
        }

        try {
            // Handle external URLs such as Education courses
            if (card?.url.startsWith('http') || card?.url.startsWith('www')) {
                const cardUrl = (card.url.startsWith('www') ? 'https://' : '') + card.url;
                window.open(cardUrl, '_blank');
            } else {
                // Handle internal navigation for Starter Kits and Capability Accelerators
                this.router.navigateByUrl(card.url);
            }
        } catch (error) {
            console.error('Error navigating to card URL:', error);
        }
    }

    /**
     * Unsubscribe from all subscriptions on destroy
     * @returns void
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
